import { useFormik } from "formik";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createPolicyConfig } from "../../redux/slice/policy/policySlice";
import MultiSelect from "../form/select/MultiSelect";
import Select from "../form/select/Select";
import PolicyPreview from "../modals/preview/PolicyPreview";
import { flashMessage as flash } from "./../../utils/flash/index";
import {
  getEmptyKey,
  isEmptyObject,
  isEmptyObjectValues,
  objectCheck,
} from "../../utils/helpers/filter";

const Settings = ({
  accountingRef,
  policyConfig,
  coverRef,
  genInfoRef,
  interestRef,
  policyCreateCloseRef,
}) => {
  const {
    offices: { offices },
  } = useSelector((state) => state?.office);
  const charges = ["ID Card", "Passport", "License", "TIN"];

  const [subDisable, setSubDisable] = useState(false);
  const [specOfficeDisable, setSpecOfficeDisable] = useState(false);
  const [data, setData] = useState(null);

  const rabRef = useRef();
  const subOfficeRef = useRef(null);
  const soRef = useRef(null);
  const preview = useRef();

  const onSubmit = (values) => {
    const arr = values?.charges
      ? values?.charges?.map((item) => ({
          charge_id: item?.value,
        }))
      : [];

    let payload = {
      charges: [...arr],
      expires_at: values?.expires_at,
    };

    // structuring data to fit backend request
    if (values?.apply_to === "CW") {
      payload = {
        ...payload,
        company_wide: true,
      };
    } else if (values?.apply_to === "RAB") {
      if (!values?.rab)
        return flash("error", "A region/area/branch must be selected to apply");
      payload = {
        ...payload,
        rab: values?.rab,
        apply_sub_offices: values?.apply_sub_offices,
      };
    } else {
      const specific_offices = values?.specific_offices?.map((item) => ({
        office_id: item?.value,
      }));

      payload = {
        ...payload,
        specific_offices: [...specific_offices],
      };
    }

    // get all required fields names that are empty
    let emptyPI = getEmptyKey(policyConfig?.p_general_information);
    let emptyPR = getEmptyKey(policyConfig?.p_interest);

    // check whether all required fields have been filled
    if (emptyPI !== "description" && emptyPI !== null) {
      flash("error", `${emptyPI} is required`);
      genInfoRef?.current?.click();
      return;
    }

    // check whether minimum age is greater than maximum age
    if (
      parseInt(policyConfig?.p_general_information?.minimum_age) >
      parseInt(policyConfig?.p_general_information?.maximum_age)
    ) {
      genInfoRef?.current?.click();
      return;
    }

    // check if there atleast one cover for. If not, go to cover for tab.
    if (policyConfig?.p_cover?.every((item) => item === "")) {
      coverRef?.current?.click();
      flash("error", "Atleast one cover is required");
      return;
    }

    // check if minimum rate is not greater than maximum rate and default rate not greater than less or greater than minimum and maximum respectively
    if (
      parseFloat(policyConfig?.p_interest?.default_interest_rate) <
        parseFloat(policyConfig?.p_interest?.minimum_interest_rate) ||
      parseFloat(policyConfig?.p_interest?.default_interest_rate) >
        parseFloat(policyConfig?.p_interest?.maximum_interest_rate)
    ) {
      interestRef?.current?.click();
      return;
    }

    // Check for rate for any empty fields since all are required.
    if (emptyPR) {
      interestRef?.current?.click();
      flash("error", `${emptyPR} is required`);
      return;
    }

    // check if atleast one GL account has been selected
    if (objectCheck(policyConfig?.p_account)) {
      accountingRef?.current?.click();
      flash("error", "Atleast one account GL is required");
      return;
    }

    // send data to preview modal
    setData({ ...policyConfig, payload, ...values });
    preview?.current?.click();
  };

  const {
    values,
    errors,
    touched,
    handleBlur,
    handleChange,
    handleSubmit,
    setFieldValue,
    getFieldProps,
    setFieldTouched,
  } = useFormik({
    enableReinitialize: true,
    initialValues: {
      apply_to: "CW",
      apply_sub_offices: "",
      specific_offices: [],
      rab: "",
      expires_at: "",
      policy_cat_id: 1,
    },
    validate: (values) => {
      const errors = {};
      if (
        (!values.specific_offices || values.specific_offices.length === 0) &&
        values?.apply_to === "SO"
      ) {
        errors.specific_offices = "At least one specific office is required";
      }
      if (values?.apply_to === "RAB" && !values?.rab) {
        errors.rab = "Region/Area/Branch is required";
      }
      return errors;
    },
    onSubmit,
  });

  useEffect(() => {
    // controls the switching of the radio buttons on settings tab.
    // Also clearing of fields previously selected but later unselected
    if (values?.apply_to === "RAB") {
      setFieldTouched("specific_offices", false);
      soRef?.current?.clearValue();
      setSubDisable(false);
      rabRef?.current?.clearValue();
      setSpecOfficeDisable(true);
    } else if (values?.apply_to === "CW") {
      setFieldTouched("rab", false);
      rabRef?.current?.clearValue();
      soRef?.current?.clearValue();
      if (values?.apply_sub_offices) {
        subOfficeRef?.current?.click();
      }
      setSubDisable(true);
      setSpecOfficeDisable(true);
    } else {
      soRef?.current.clearValue();
      rabRef?.current?.clearValue();
      setFieldTouched("rab", false);
      if (values?.apply_sub_offices) {
        subOfficeRef?.current?.click();
      }
      setSubDisable(true);
    }
  }, [values?.apply_to]);

  return (
    <>
      <form onSubmit={handleSubmit}>
        <div className=''>
          <h5>Apply to (Office)</h5>
          <div class='form-check my-3'>
            <input
              class='form-check-input me-2'
              type='radio'
              name='apply_to'
              id='apply_to'
              value='CW'
              onChange={handleChange}
              defaultChecked={values?.apply_to === "CW"}
            />
            <label class='form-check-label' for='company_wide'>
              Company wide
            </label>
          </div>
          <div class='form-check'>
            <input
              class='form-check-input me-2'
              type='radio'
              name='apply_to'
              value='RAB'
              onChange={handleChange}
              id='apply_to'
            />
            <label class='form-check-label' for='rab'>
              Region/Area/Branch
            </label>
            {values?.apply_to === "RAB" && (
              <div>
                <Select
                  isError={errors?.rab && touched?.rab}
                  name='rab'
                  ref={rabRef}
                  data={offices?.map((item, i) => ({
                    label: item?.office_name,
                    value: item?.id,
                  }))}
                  value={values?.rab}
                  onChange={(selectedOption) =>
                    setFieldValue("rab", selectedOption?.value)
                  }
                  onBlur={() => {
                    handleBlur({ target: { name: "rab" } });
                  }}
                  disabled={values?.apply_to !== "RAB"}
                />
                {errors?.rab && touched?.rab && (
                  <p className='text-danger mb-0'>{errors?.rab}</p>
                )}
                <div class='form-check mt-1'>
                  <input
                    class='form-check-input'
                    type='checkbox'
                    value={values?.apply_sub_offices}
                    id='apply_sub_offices'
                    name='apply_sub_offices'
                    onChange={handleChange}
                    ref={subOfficeRef}
                    disabled={values?.apply_to !== "RAB" && subDisable}
                  />
                  <label class='form-check-label' for=''>
                    Apply to sub offices
                  </label>
                </div>
              </div>
            )}
          </div>
          <div class='form-check mt-3'>
            <input
              class='form-check-input me-2'
              type='radio'
              name='apply_to'
              value='SO'
              onChange={handleChange}
              id='apply_to'
            />
            <label class='form-check-label ' for='so'>
              Specific Office(s)
            </label>
            {values?.apply_to === "SO" && (
              <div>
                <MultiSelect
                  isError={
                    errors?.specific_offices && touched?.specific_offices
                  }
                  name='specific_offices'
                  field={{ ...getFieldProps("specific_offices") }}
                  data={offices?.map((item, i) => ({
                    label: item?.office_name,
                    value: item?.id,
                  }))}
                  onChange={setFieldValue}
                  onBlur={() => {
                    handleBlur({ target: { name: "specific_offices" } });
                  }}
                  errors={errors}
                  touched={touched}
                  disabled={values?.apply_to !== "SO"}
                  ref={soRef}
                />
              </div>
            )}
          </div>
          <div class='form-check mt-3'>
            <label class='form-check-label ' for='so'>
              Add charges
            </label>
            <div>
              <MultiSelect
                isError={errors?.charges && touched?.charges}
                name='charges'
                field={{ ...getFieldProps("charges") }}
                data={charges?.map((item, i) => ({
                  label: item,
                  value: i + 1,
                }))}
                onChange={setFieldValue}
                onBlur={() => {
                  handleBlur({ target: { name: "charges" } });
                }}
                errors={errors}
                touched={touched}
                // ref={soRef}
              />
            </div>
          </div>
          <div class='form-check mt-3 mb-4'>
            <label class='form-check-label ' for='so'>
              Expires at
            </label>
            <div>
              <input
                name='expires_at'
                value={values?.expires_at}
                className='form-control'
                type='date'
                onChange={handleChange}
              />
            </div>
          </div>
          <div className='d-flex justify-content-end'>
            <button
              type='button'
              className='btn btn-info text-white me-3'
              onClick={() => accountingRef?.current?.click()}
            >
              <i className='mdi mdi-arrow-left me-1'></i>
              Previous
            </button>
            <button type='submit' className='btn btn-primary text-white '>
              Submit <i className='mdi mdi-arrow-right ms-1'></i>
            </button>
          </div>
        </div>
      </form>
      <button
        ref={preview}
        className='d-none'
        data-bs-toggle='modal'
        data-bs-target='#preview-policy'
      ></button>
      <PolicyPreview
        modal_id={"preview-policy"}
        data={data}
        policyCreateCloseRef={policyCreateCloseRef}
      />
    </>
  );
};

export default Settings;
